<script>
    import { createEventDispatcher, onMount, onDestroy } from 'svelte';
    import { fade } from 'svelte/transition';
    import { createFocusTrap } from 'focus-trap';

    const dispatch = createEventDispatcher();

    // Props
    export let show = false;
    export let title = '';
    export let titleId = 'modal-title';
    export let closeOnBackdropClick = true;
    export let variant = 'spec'; // 'spec' | 'terms' | 'claims' | 'neutral'

    // Ref to the dialog element
    let dialogEl;
    let focusTrapInstance;
    
    // Track the last active element before opening the modal for proper focus restoration
    let lastActiveElement;

    // Handle the Escape key globally while the modal is open
    function handleKeydown(event) {
        if (event.key === 'Escape' && show) {
            closeModal();
        }
    }

    // Close the modal
    function closeModal() {
        show = false;
        dispatch('close');
    }

    // Handle backdrop click with a button element
    function handleBackdropClick() {
        if (closeOnBackdropClick) {
            closeModal();
        }
    }

    // Set up and clean up keydown listener
    $: if (show) {
        window.addEventListener('keydown', handleKeydown);
    } else {
        window.removeEventListener('keydown', handleKeydown);
    }

    // Manage focus trap when modal opens/closes
    $: if (show && dialogEl) {
        // Save the currently focused element
        lastActiveElement = document.activeElement;
        
        // Wait for the modal to render
        setTimeout(() => {
            if (dialogEl && !focusTrapInstance) {
                // Create focus trap
                focusTrapInstance = createFocusTrap(dialogEl, {
                    escapeDeactivates: false, // We handle Escape ourselves
                    clickOutsideDeactivates: false, // We handle outside clicks ourselves
                    returnFocusOnDeactivate: false, // We handle focus restoration ourselves
                    fallbackFocus: dialogEl,
                    allowOutsideClick: true, // Allow clicks on the backdrop
                    preventScroll: true,
                    initialFocus: () => {
                        // Try to focus the first focusable element
                        const firstFocusable = dialogEl.querySelector('button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
                        return firstFocusable || dialogEl;
                    }
                });
                
                focusTrapInstance.activate();
            }
        }, 100); // Increased delay for cross-browser compatibility

        // Prevent body scrolling
        document.body.style.overflow = 'hidden';
    } else {
        // Deactivate focus trap
        if (focusTrapInstance) {
            focusTrapInstance.deactivate();
            focusTrapInstance = null;
        }
        
        // Restore focus when modal closes
        if (lastActiveElement) {
            setTimeout(() => {
                lastActiveElement.focus();
            }, 0);
        }
        
        // Restore body scrolling
        document.body.style.overflow = '';
    }

    // Clean up on component destruction
    onDestroy(() => {
        window.removeEventListener('keydown', handleKeydown);
        document.body.style.overflow = '';
        if (focusTrapInstance) {
            focusTrapInstance.deactivate();
            focusTrapInstance = null;
        }
    });
</script>

{#if show}
    <div 
        class="modal-wrapper fixed top-0 left-0 w-full h-full flex-center z-modal"
        transition:fade={{ duration: 200 }}
    >
        <!-- 
            Use a button for the backdrop to make it accessible
            This allows keyboard interaction and proper role assignment
        -->
        <button 
            class="modal-backdrop absolute top-0 left-0 w-full h-full p-0 m-0 cursor-pointer z-modal-backdrop" 
            on:click={handleBackdropClick}
            aria-label="Close modal"
            tabindex="-1"
        ></button>
        
        <!-- 
            The dialog itself, properly accessible with ARIA attributes
            We don't need click handlers on the dialog element itself
        -->
        <dialog
            bind:this={dialogEl}
            class="modal-container modal-variant-{variant} relative p-0 z-modal" 
            open={show}
            aria-modal="true"
            aria-labelledby={titleId}
            tabindex="-1"
            {...$$restProps}
        >
            <!-- Slot for the entire modal content -->
            <slot name="content">
                <!-- Default modal structure if no content slot is provided -->
                <div class="modal-header flex flex-justify-between flex-align-center sticky top-0" style="z-index: 100;">
                    <h2 id={titleId} class="m-0">{title}</h2>
                    <button 
                        class="close-button cursor-pointer" 
                        on:click={closeModal} 
                        aria-label="Close"
                    >×</button>
                </div>
                <div class="modal-body">
                    <slot></slot>
                </div>
            </slot>
        </dialog>
    </div>
{/if}

<style>
    /* Custom scrollbar styling - scoped to modal content */
    .modal-container :global(::-webkit-scrollbar) {
        width: 6px;
        height: 6px;
    }
    
    .modal-container :global(::-webkit-scrollbar-track) {
        background: var(--color-scrollbar-track);
    }
    
    .modal-container :global(::-webkit-scrollbar-thumb) {
        background: var(--color-scrollbar-thumb);
        border-radius: 3px;
    }
    
    .modal-container :global(::-webkit-scrollbar-thumb:hover) {
        background: var(--color-scrollbar-thumb-hover);
    }
    
    /* Firefox scrollbar styling */
    .modal-container {
        scrollbar-width: thin;
        scrollbar-color: var(--color-scrollbar-thumb) var(--color-scrollbar-track);
    }

    /* Using utility classes for modal-wrapper */

    .modal-backdrop {
        background-color: var(--color-backdrop);
        border: none;
    }

    .modal-container {
        width: 90%;
        max-width: 800px;
        max-height: 85vh;
        margin: auto;
        overflow-y: auto;
        border: none;
        border-radius: var(--border-radius-3xl);
        box-shadow: var(--shadow-modal);
    }
    
    /* Variant-specific backgrounds */
    .modal-variant-neutral {
        background-color: var(--color-surface-content-neutral);
    }
    
    .modal-variant-spec {
        background-color: var(--color-surface-content-spec);
    }
    
    .modal-variant-terms {
        background-color: var(--color-surface-content-terms);
    }
    
    .modal-variant-claims {
        background-color: var(--color-surface-content-claims);
    }

    .modal-header {
        padding: var(--spacing-6);
        border-bottom: var(--border-default);
        opacity: 1.0;
        backdrop-filter: blur(8px);
        box-shadow: var(--shadow-sm);
    }
    
    /* Variant-specific header backgrounds */
    .modal-variant-neutral .modal-header {
        background-color: var(--color-surface-header-neutral);
    }
    
    .modal-variant-spec .modal-header {
        background-color: var(--color-surface-header-spec);
    }
    
    .modal-variant-terms .modal-header {
        background-color: var(--color-surface-header-terms);
    }
    
    .modal-variant-claims .modal-header {
        background-color: var(--color-surface-header-claims);
    }

    .modal-header h2 {
        font-size: var(--font-size-2xl);
        color: var(--color-text-primary);
    }

    .close-button {
        padding: var(--spacing-2);
        font-size: var(--font-size-2xl);
        color: var(--color-text-secondary);
        background: none;
        border: none;
        transition: color var(--duration-fast) var(--ease-in-out);
    }

    .close-button:hover {
        color: var(--color-text-primary);
    }

    .modal-body {
        padding: var(--spacing-6);
    }
</style>